/**
 * Wraps email body (plain text or HTML) into a sendable HTML email.
 *
 * Two designs are supported:
 *  - "standard" : the branded Thiia card (purple header bar, logo, footer).
 *  - "plain"    : a minimal, neutral, unbranded HTML wrapper. Looks like a
 *                 regular email — no Thiia logo, no colored header, no footer.
 *                 Use this when sending on behalf of a client/brand that
 *                 should not show "Thiia" anywhere.
 */

import { emailListStyleBlock } from "./emailListStyles";

export type EmailDesign = "standard" | "plain";

const LOGO_URL = "https://meet-matheo-ai.lovable.app/favicon.png";
const PRIMARY = "#7B2D8E";
const ACCENT = "#B39DDB";
const BG_OUTER = "#f3f0f7";
const BG_CARD = "#ffffff";
const TEXT_SECONDARY = "#555566";
const TEXT_MUTED = "#8e8ea0";

function bodyToHtml(body: string, color = TEXT_SECONDARY): string {
  const isHtml = /<[a-z][\s\S]*>/i.test(body);
  if (isHtml) return body;
  const escaped = body
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
  return escaped
    .split(/\n\n+/)
    .map((paragraph) => {
      const lines = paragraph.split("\n").join("<br>");
      return `<p style="margin:0 0 12px 0;line-height:1.65;font-size:15px;color:${color};">${lines}</p>`;
    })
    .join("");
}

export function wrapEmailHtml(
  body: string,
  senderNameOrDesign?: string | EmailDesign,
  maybeDesign?: EmailDesign,
): string {
  // Backwards-compatible signature: previously `wrapEmailHtml(body, senderName?)`.
  // Now accepts either `(body, design)` or `(body, senderName, design)`.
  const design: EmailDesign =
    maybeDesign ??
    (senderNameOrDesign === "plain" || senderNameOrDesign === "standard"
      ? (senderNameOrDesign as EmailDesign)
      : "standard");

  if (design === "plain") return wrapPlain(body);
  return wrapStandard(body);
}

function wrapPlain(body: string): string {
  const htmlBody = bodyToHtml(body, "#222222");
  // The <!-- thiia:plain --> marker tells backend senders to skip the
  // visible legal/unsubscribe footer (List-Unsubscribe headers are still set).
  return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  ${emailListStyleBlock()}
</head>
<body style="margin:0;padding:0;background-color:#ffffff;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;color:#222222;font-size:15px;line-height:1.65;text-align:left;">
  <!-- thiia:plain -->
  <div class="email-body" style="max-width:640px;margin:0;padding:12px;text-align:left;">
    ${htmlBody}
  </div>
</body>
</html>`;
}

function wrapStandard(body: string): string {
  const htmlBody = bodyToHtml(body);
  return `<!DOCTYPE html>
<html lang="fr" xmlns="http://www.w3.org/1999/xhtml">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta name="color-scheme" content="light dark">
  <meta name="supported-color-schemes" content="light dark">
  <style>
    :root { color-scheme: light dark; supported-color-schemes: light dark; }
    body { margin:0; padding:0; width:100%; }
    @media (prefers-color-scheme: dark) {
      .email-bg { background-color:#1a1a2e !important; }
      .email-card { background-color:#252540 !important; }
      .text-main { color:#e8e8f0 !important; }
      .text-muted { color:#8888a8 !important; }
    }
  </style>
  ${emailListStyleBlock()}
</head>
<body style="margin:0;padding:0;background-color:${BG_OUTER};font-family:'Plus Jakarta Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" class="email-bg" style="background-color:${BG_OUTER};padding:32px 16px;">
    <tr>
      <td align="center">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:700px;">
          <!-- Logo -->
          <tr>
            <td align="center" style="padding:0 0 24px;">
              <a href="https://meet-matheo-ai.lovable.app" style="text-decoration:none;display:inline-block;">
                <img src="${LOGO_URL}" alt="Thiia" width="64" height="64" style="width:64px;height:64px;border-radius:16px;display:block;">
              </a>
            </td>
          </tr>

          <!-- Card -->
          <tr>
            <td>
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" class="email-card" style="background-color:${BG_CARD};border-radius:16px;overflow:hidden;box-shadow:0 1px 12px rgba(0,0,0,0.06);">
                <tr>
                  <td style="height:4px;background:linear-gradient(90deg,${PRIMARY},${ACCENT});"></td>
                </tr>
                <tr>
                  <td class="email-body text-main" style="padding:28px 32px;color:#1a1a2e;font-size:15px;line-height:1.65;">
                    ${htmlBody}
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <!-- Footer -->
          <tr>
            <td align="center" style="padding:20px 0 0;">
              <p class="text-muted" style="margin:0;font-size:12px;color:${TEXT_MUTED};line-height:1.5;">© ${new Date().getFullYear()} Thiia — Votre assistant IA</p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}
